import React, { useCallback, useEffect, useState } from "react";
import { Button, OverlayTrigger, Table, Tooltip } from "react-bootstrap";
import { toast } from "react-toastify";
import Loading from "../../../components/LoadingPage/Loading";
import CommonPagination from "../../../global/Pagination/CommonPagination";
import useMckesson from "../../../Hooks/useMckesson";
import "./McItemsMaster.scss";

interface McItemRecord {
  id: string;
  itemId: string;
  description: string;
  supplierId: string;
  manufacturerId: string;
  manufacturerName: string;
  atomicUom: string;
  valid: boolean;
}

interface McItemUom {
  id: string;
  isActive: boolean;
  createdOnUtc: string;
  updatedOnUtc: string;
  createdBy: string;
  updatedBy: string;
  mcKessonItemId: string;
  currency: string;
  price: number;
  quantityAvailable: number;
  per: number;
  atomic: number;
  units: string;
}

type SearchMode = "lookup" | "itemSearch" | "itemFeed";

const defaultFilter = { searchText: "", active: true };

const McItemsMaster: React.FC = () => {
  const [searchVal, setSearchVal] = useState({ ...defaultFilter });
  const [items, setItems] = useState<McItemRecord[]>([]);
  const [uomData, setUomData] = useState<McItemUom[]>([]);
  const [selectedItemId, setSelectedItemId] = useState<string>("");
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(100);
  const [itemId, setItemId] = useState<string>("");
  const [inputError, setInputError] = useState<string>("");
  const [sortColumn, setSortColumn] = useState<string>("ItemId");
  const [sortDirection, setSortDirection] = useState<string>("asc");
  const [searchMode, setSearchMode] = useState<SearchMode>("lookup");
  const [useInternet, setUseInternet] = useState<boolean>(false);
  const [persist, setPersist] = useState<boolean>(true);
  const [searchText, setSearchText] = useState<string>("");
  const isAdmin = localStorage.getItem("roleName")?.toLowerCase() === "sysadmin";

  const {
    getMckessonItemsAPI,
    getMckessonItemsLoading,
    getMckessonUomsAPI,
    getMckessonUomsLoading,
    postMckessonItemSearchAPI,
    postMckessonItemSearchLoading,
    postMckessonItemFeedAPI,
    postMckessonItemFeedLoading,
    postMckessonItemLookupAPI,
    postMckessonItemLookupLoading,
  } = useMckesson();

  const isLoading = getMckessonItemsLoading || getMckessonUomsLoading || postMckessonItemSearchLoading || postMckessonItemFeedLoading || postMckessonItemLookupLoading;

  const loadItems = useCallback(async (page: number = 1, overrideSearchText?: string, overrideItemId?: string) => {
    try {
      const response = await getMckessonItemsAPI({
        searchText: overrideSearchText ?? searchVal.searchText,
        itemId: overrideItemId ?? itemId,
        active: searchVal.active,
        sortBy: sortColumn,
        isDescending: sortDirection === "desc",
        pageNumber: page,
        pageSize: pageSize
      });
      if (response) {
        setItems(response.result || []);
        let total = response.result?.length || 0;
        if (response.message) {
          const match = response.message.match(/Total Records:\s*(\d+)/);
          if (match) {
            total = parseInt(match[1], 10);
          }
        }
        setTotalRecords(total);
        setUomData([]);
        setSelectedItemId("");
      }
    } catch (error: any) {
      toast.error(error.message);
    }
  }, [searchVal, pageSize, itemId, sortColumn, sortDirection]);

  useEffect(() => {
    setCurrentPage(1);
    loadItems(1);
  }, [loadItems]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    loadItems(page);
  };

  const handleSearch = async () => {
    const value = searchText.trim();

    if (searchMode === "lookup" && !value) {
      setInputError("Item Id is required for Lookup");
      return;
    }

    setInputError("");

    try {
      if (searchMode === "lookup") {
        if (useInternet) {
          // Scenario 1: Lookup + Internet → McKesson Lookup API
          await postMckessonItemLookupAPI({ itemId: value, persist });
          setCurrentPage(1);
          loadItems(1);
        } else {
          // Scenario 2: Lookup + No Internet → Local search by itemId
          setItemId(value);
          setSearchVal({ ...searchVal, searchText: "" });
          setCurrentPage(1);
          loadItems(1, "", value);
        }
      } else if (searchMode === "itemSearch") {
        if (useInternet) {
          // Scenario 3: Item Search + Internet → McKesson Item Search API
          await postMckessonItemSearchAPI({
            query: value,
            pageSize: 100,
            offset: 0,
            sort: { order: "asc", by: "ItemId" },
            persist,
          });
          setCurrentPage(1);
          loadItems(1);
        } else {
          // Scenario 4: Item Search + No Internet → Local search by searchText
          setItemId("");
          setSearchVal({ ...searchVal, searchText: value });
          setCurrentPage(1);
          loadItems(1, value, "");
        }
      } else if (searchMode === "itemFeed") {
        if (useInternet) {
          // Scenario 5: Item Feed + Internet → McKesson Item Feed API
          await postMckessonItemFeedAPI({
            source: { type: "", text: value },
            pageSize: 100,
            offset: 0,
            persist,
          });
          setCurrentPage(1);
          loadItems(1);
        } else {
          // Scenario 6: Item Feed + No Internet → Local search by searchText
          setItemId("");
          setSearchVal({ ...searchVal, searchText: value });
          setCurrentPage(1);
          loadItems(1, value, "");
        }
      }
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  const handleModeChange = (mode: SearchMode) => {
    setSearchMode(mode);
    setSearchText("");
    setInputError("");
  };

  const handleRowClick = async (item: McItemRecord) => {
    setSelectedItemId(item.id);
    try {
      const result = await getMckessonUomsAPI({
        mcKessonItemId: item.id,
        searchText: "",
        active: true,
      });
      if (result) {
        setUomData(result);
      } else {
        setUomData([]);
      }
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  const handleSort = (columnName: string) => {
    if (sortColumn === columnName) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(columnName);
      setSortDirection("asc");
    }
  };

  return (
    <div className="mc-items-master-wrapper">
      {isLoading && <Loading />}

      <div className="mc-items-header">
        <div className="listing-title-info">
          <h2>McKesson Items Master</h2>
          <p>Displaying {items.length} of {totalRecords} Results</p>
        </div>

        <div className="mc-items-search-controls">
          <div className="mc-radio-group">
            <label className="mc-radio-label">
              <input
                type="radio"
                name="searchMode"
                checked={searchMode === "lookup"}
                onChange={() => handleModeChange("lookup")}
              />
              Item Id
            </label>
            <label className="mc-radio-label">
              <input
                type="radio"
                name="searchMode"
                checked={searchMode === "itemSearch"}
                onChange={() => handleModeChange("itemSearch")}
              />
              Item Name
            </label>
            {isAdmin && (
              <OverlayTrigger
                placement="top"
                overlay={<Tooltip id="tooltip-item-feed">Load Full Feed Data</Tooltip>}
              >
                <label className="mc-radio-label">
                  <input
                    type="radio"
                    name="searchMode"
                    checked={searchMode === "itemFeed"}
                    onChange={() => handleModeChange("itemFeed")}
                  />
                  Item Feed
                </label>
              </OverlayTrigger>
            )}
          </div>

          <label className="mc-checkbox-label">
            <input
              type="checkbox"
              checked={useInternet}
              onChange={(e) => setUseInternet(e.target.checked)}
            />
            Mckesson
          </label>

          <label className="mc-checkbox-label">
            <input
              type="checkbox"
              checked={persist}
              onChange={(e) => setPersist(e.target.checked)}
            />
            Persist
          </label>

          <div className="mc-search-input">
            <input
              type={searchMode === "lookup" ? "number" : "text"}
              className={`form-control${inputError ? " is-invalid" : ""}`}
              placeholder={searchMode === "lookup" ? "Item Id" : "Search"}
              value={searchText}
              onChange={(e) => {
                setSearchText(e.target.value);
                if (e.target.value.trim()) setInputError("");
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSearch();
              }}
              autoFocus
            />
            {inputError && (
              <div className="invalid-feedback d-block">{inputError}</div>
            )}
          </div>

          <Button variant="primary" onClick={handleSearch}>
            Search
          </Button>

          <Button
            variant="outline-secondary"
            className="btn-search"
            onClick={() => {
              setSearchText("");
              setItemId("");
              setSearchVal({ ...defaultFilter });
              setInputError("");
              setCurrentPage(1);
              setUseInternet(false);
              setPersist(true);
            }}
          >
            Clear All / Refresh
          </Button>
        </div>

        <div className="mc-page-size">
          <label htmlFor="mcPageSize">Page Size:</label>
          <input
            id="mcPageSize"
            type="number"
            className="form-control"
            value={pageSize}
            onChange={(e) => {
              const val = parseInt(e.target.value, 10);
              if (val > 0) {
                setPageSize(val);
                setCurrentPage(1);
              }
            }}
          />
        </div>
      </div>

      {/* Items Table */}
      <div className="listing-table-wrapper">
        <Table responsive="md" hover>
          <thead>
            <tr>
              <th className="cursor-pointer" onClick={() => handleSort('ItemId')}>Item Id {sortColumn === 'ItemId' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
              <th className="cursor-pointer" onClick={() => handleSort('Description')}>Description {sortColumn === 'Description' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
              <th className="cursor-pointer" onClick={() => handleSort('SupplierId')}>Supplier Id {sortColumn === 'SupplierId' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
              <th className="cursor-pointer" onClick={() => handleSort('ManufacturerId')}>Manufacturer Id {sortColumn === 'ManufacturerId' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
              <th className="cursor-pointer" onClick={() => handleSort('ManufacturerName')}>Manufacturer Name {sortColumn === 'ManufacturerName' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
              <th className="cursor-pointer" onClick={() => handleSort('AtomicUom')}>Atomic Uom {sortColumn === 'AtomicUom' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
              <th className="cursor-pointer" onClick={() => handleSort('Valid')}>Valid? {sortColumn === 'Valid' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
            </tr>
          </thead>
          <tbody>
            {items.length > 0 ? (
              items.map((item) => (
                <tr
                  key={item.id}
                  className={selectedItemId === item.id ? "selected" : ""}
                  onClick={() => handleRowClick(item)}
                >
                  <td>{item.itemId}</td>
                  <td>{item.description}</td>
                  <td>{item.supplierId}</td>
                  <td>{item.manufacturerId}</td>
                  <td>{item.manufacturerName}</td>
                  <td>{item.atomicUom}</td>
                  <td>{item.valid ? "Yes" : "No"}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={7} className="text-center">
                  No records found
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      <CommonPagination
        totalRecords={totalRecords}
        onChange={handlePageChange}
        perPage={pageSize}
        currentPage={currentPage}
      />

      {/* UOM Detail Table */}
      {uomData.length > 0 && (
        <div className="mc-items-pricing-section">
          <div className="listing-table-wrapper pricing-table">
            <Table responsive="md" hover>
              <thead>
                <tr>
                  <th>Quantity Available</th>
                  <th>Price</th>
                  <th>Currency</th>
                  <th>Per</th>
                  <th>Atomic</th>
                  <th>Units</th>
                  <th>Created By</th>
                  <th>Created Date</th>
                </tr>
              </thead>
              <tbody>
                {uomData.map((uom, index) => (
                  <tr key={index}>
                    <td>{uom.quantityAvailable?.toFixed(4)}</td>
                    <td>{uom.price?.toFixed(2)}</td>
                    <td>{uom.currency}</td>
                    <td>{uom.per?.toFixed(2)}</td>
                    <td>{uom.atomic?.toFixed(2)}</td>
                    <td>{uom.units}</td>
                    <td>{uom.createdBy}</td>
                    <td>{uom.createdOnUtc}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </div>
      )}
    </div>
  );
};

export default McItemsMaster;
